import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Smartphone, Send, Apple, Monitor, CheckCircle, XCircle } from 'lucide-react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ROLE_LABELS, pct } from '@/components/AppAdoptionWidget';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import API from '@/config/api';

const ROLE_KEY = { parent: 'parents', provider: 'staff', driver: 'drivers' };
const PLATFORM = { ios: ['iPhone', Apple], android: ['Android', Smartphone], desktop: ['Computer', Monitor] };

const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

const StatCards = ({ counts, platforms }) => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
    {ROLE_LABELS.map(([key, label]) => (
      <Card key={key} className="card-clay" data-testid={`adoption-stat-${key}`}>
        <CardContent className="p-5">
          <p className="text-sm text-muted-foreground">{label} installed</p>
          <p className="font-heading font-bold text-3xl">{counts[key].installed}<span className="text-lg text-muted-foreground">/{counts[key].total}</span></p>
          <Progress value={pct(counts[key])} className="h-2 mt-3" />
          <p className="text-xs text-muted-foreground mt-2">{pct(counts[key])}% adoption</p>
        </CardContent>
      </Card>
    ))}
    <Card className="card-clay" data-testid="adoption-stat-platforms">
      <CardContent className="p-5 space-y-2">
        <p className="text-sm text-muted-foreground">Devices by type</p>
        {Object.entries(PLATFORM).map(([k, [label, Icon]]) => (
          <div key={k} className="flex items-center justify-between text-sm" data-testid={`adoption-platform-${k}`}>
            <span className="flex items-center gap-2"><Icon className="w-4 h-4 text-primary" />{label}</span>
            <span className="font-bold">{platforms[k] || 0}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  </div>
);

const PersonRow = ({ p }) => (
  <tr className="border-b last:border-0" data-testid={`adoption-row-${p.id}`}>
    <td className="py-3 pr-4"><p className="font-semibold">{p.name}</p><p className="text-xs text-muted-foreground">{p.email}</p></td>
    <td className="py-3 pr-4 capitalize">{ROLE_KEY[p.role] === 'staff' ? 'Staff' : p.role}</td>
    <td className="py-3 pr-4">
      {p.installed
        ? <Badge className="bg-green-100 text-green-700 hover:bg-green-100"><CheckCircle className="w-3 h-3 mr-1" />Installed</Badge>
        : <Badge variant="outline" className="text-muted-foreground"><XCircle className="w-3 h-3 mr-1" />Not yet</Badge>}
    </td>
    <td className="py-3 pr-4 text-sm">{p.platforms.map((k) => PLATFORM[k][0]).join(', ') || '—'}</td>
    <td className="py-3 text-sm text-muted-foreground">{fmt(p.last_seen)}</td>
  </tr>
);

export default function AppAdoptionPage() {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('all');
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/app-installs/summary`, getAuthHeaders()).then((r) => setData(r.data)).catch(() => toast.error('Could not load app adoption'));
  }, [getAuthHeaders]);
  useEffect(load, [load]);

  const people = useMemo(() => (data?.people || [])
    .filter((p) => tab === 'all' || (tab === 'missing' ? !p.installed : ROLE_KEY[p.role] === tab))
    .sort((a, b) => (a.installed - b.installed) || (a.name || '').localeCompare(b.name || '')), [data, tab]);
  const missing = (data?.people || []).filter((p) => !p.installed).length;

  const sendInvites = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/app-installs/send-invites`, {}, getAuthHeaders());
      toast.success(r.data.demo
        ? `Demo account: ${r.data.recipients} people would get the install link (no emails sent)`
        : `Install link emailed to ${r.data.sent} of ${r.data.recipients} people`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send install links');
    } finally {
      setSending(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="app-adoption-page">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-3xl">Mobile App Adoption</h1>
            <p className="text-muted-foreground">Who has installed the CareKids WI app. Counted the next time someone opens the installed app.</p>
          </div>
          <div className="text-right">
            <Button className="btn-primary" onClick={sendInvites} disabled={sending || !missing} data-testid="send-install-invites-btn">
              <Send className="mr-2 w-4 h-4" />{sending ? 'Sending…' : `Send install link (${missing})`}
            </Button>
            {data?.last_invite && <p className="text-xs text-muted-foreground mt-1" data-testid="last-invite-info">Last sent {fmt(data.last_invite.sent_at)} to {data.last_invite.recipients} people</p>}
          </div>
        </div>
        {data && <StatCards counts={data.counts} platforms={data.platforms} />}
        <Card className="card-clay">
          <CardContent className="p-5">
            <Tabs value={tab} onValueChange={setTab} className="mb-4">
              <TabsList className="flex flex-wrap h-auto justify-start" data-testid="adoption-tabs">
                <TabsTrigger value="all" data-testid="adoption-tab-all">Everyone</TabsTrigger>
                <TabsTrigger value="missing" data-testid="adoption-tab-missing">Not installed</TabsTrigger>
                {ROLE_LABELS.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`adoption-tab-${k}`}>{l}</TabsTrigger>)}
              </TabsList>
            </Tabs>
            <div className="overflow-x-auto">
              <table className="mobile-cards w-full text-left" data-testid="adoption-table">
                <thead><tr className="text-xs uppercase text-muted-foreground border-b">
                  <th className="pb-2 pr-4">Name</th><th className="pb-2 pr-4">Role</th><th className="pb-2 pr-4">Status</th><th className="pb-2 pr-4">Device</th><th className="pb-2">Last opened</th>
                </tr></thead>
                <tbody>{people.map((p) => <PersonRow key={p.id} p={p} />)}</tbody>
              </table>
              {data && !people.length && <p className="text-center text-muted-foreground py-8" data-testid="adoption-empty">No one here yet.</p>}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
